import {
  UserMediaErrors,
  fakePromiseDelay,
  useAppDispatch,
  useAppSelector,
  useAppTranslation,
  useDeviceDetect,
  userMediaErrors,
} from "@/utils";
import { Flex, notification, useMessagedAsyncFn } from "@payping/web-components";
import { FC, useEffect, useState } from "react";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import GetOrderUploadVideoAndSignaturePageIntro from "./intro";
import GetOrderUploadVideoAndSignaturePageStart from "./start";
import GetOrderUploadVideoAndSignaturePageAccessGuide from "./AccessGuide";
import PayGetOrderLayout from "../Layout";
import PayGetOrderUploadSignatureDraw from "../UploadSignature/Draw";
import PayExpiredOrder from "../../ExpiredOrder";
import PayGetOrderUploadSignatureUnAvailable from "../UploadSignature/UnAvailable";
import ContinueWithMobileModal from "./ContinueWithMobileModal";
import ContinueWithSupportedDevices from "./ContinueWithSupportedDevices";
import PayGetOrderIssuePromissoryFailed from "../IssuePromissory/Failed";

enum Steps {
  IMAGE_SIGNATURE = "IMAGE_SIGNATURE",
  INTRO = "INTRO",
  START = "START",
  ACCESS_GUIDE = "ACCESS_GUIDE",
}

interface UploadVideoAndSignatureProps {
  afterSuccess: () => void;
  trackingCode: string;
}

const GetOrderUploadVideoAndSignaturePage: FC<UploadVideoAndSignatureProps> = ({ afterSuccess, trackingCode }) => {
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.IMAGE_SIGNATURE);
  const [imageFile, setImageFile] = useState<File>();
  const dispatch = useAppDispatch();
  const { isDesktop, isXiaomiBrowser } = useDeviceDetect();
  const [notif, contextHolder] = notification.useNotification({ top: 16, duration: 3, placement: "top" });
  const { t } = useAppTranslation();
  const getOrderOpenInMobileModal = useAppSelector((state) => state.dashboard.getOrderOpenInMobileModal);
  const getOrderOpenInSupportedDevicesModal = useAppSelector(
    (state) => state.dashboard.getOrderOpenInSupportedDevicesModal,
  );

  const handleCloseGetOrderOpenInMobileModal = () => {
    dispatch(dashboardReduxReducerActions.setCloseGetOrderOpenInMobileModal());
  };

  const handleCloseGetOrderOpenInSupportedDevices = () => {
    dispatch(dashboardReduxReducerActions.setCloseGetOrderOpenInSupportedDevices());
  };

  useEffect(() => {
    if (isDesktop === true) {
      dispatch(dashboardReduxReducerActions.setOpenGetOrderOpenInMobileModal());
    }
    if (isDesktop === false && isXiaomiBrowser) {
      dispatch(dashboardReduxReducerActions.setOpenGetOrderOpenInSupportedDevices());
    }
  }, [dispatch, isDesktop, isXiaomiBrowser]);

  const [verifyStatus, verify] = useMessagedAsyncFn(
    async () => {
      try {
        const res = await bnplConsumerApis.checkSignature({ trackingCode }, {
          isRaw: true,
        } as SDKRequestInit);
        afterSuccess();
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e;
          }
        }
        throw e;
      }
    },
    [afterSuccess, trackingCode],
    { loading: false },
    { error: true },
  );

  const [uploadSignatureState, uploadSignature] = useMessagedAsyncFn(
    async ({
      signatureImage,
      signatureVideo,
      videoSentence,
    }: {
      signatureImage: File;
      signatureVideo: File;
      videoSentence: string;
    }) => {
      try {
        const res = await bnplConsumerApis.uploadVideoSignature(
          trackingCode,
          signatureImage,
          signatureVideo,
          videoSentence,
          signatureVideo.name,
          {
            isRaw: true,
          } as SDKRequestInit,
        );
        afterSuccess();
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            if ((parsedError as any)?.key === 103) {
              await fakePromiseDelay(50000);
              verify();
              return undefined;
            }
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e;
          }
        }
        throw e;
      }
    },
    [trackingCode, afterSuccess, verify],
    { loading: false },
    { error: true },
  );

  const handleCameraErrors = (error: UserMediaErrors) => {
    if (error === userMediaErrors.CAMERA_ACCESS_DENIED || error === userMediaErrors.CAMERA_ACCESS_NO_CAMERA_FOUND) {
      setCurrentStep(Steps.ACCESS_GUIDE);
    }
    if (error === userMediaErrors.CAMERA_ACCESS_CAMERA_IN_USE) {
      notif.error({ message: t("hooks.userMedia.CAMERA_ACCESS_CAMERA_IN_USE") });
    }
    if (error === userMediaErrors.CAMERA_ACCESS_UNKNOWN_ERROR) {
      notif.error({ message: t("hooks.userMedia.CAMERA_ACCESS_UNKNOWN_ERROR") });
    }
    if (error === userMediaErrors.CAMERA_API_DO_NOT_SUPPORT) {
      notif.error({ message: t("hooks.userMedia.CAMERA_API_DO_NOT_SUPPORT") });
    }
  };

  const handleFinishSignature = (file: File) => {
    setImageFile(file);
    setCurrentStep(Steps.INTRO);
  };

  // expired order
  if ((uploadSignatureState.error as any)?.key === 109) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayExpiredOrder />
      </PayGetOrderLayout>
    );
  }

  // error in generate issue
  if ((uploadSignatureState.error as any)?.key === 107 && (uploadSignatureState.error as any)?.detail) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderIssuePromissoryFailed
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
          message={(uploadSignatureState.error as any)?.detail}
        />
      </PayGetOrderLayout>
    );
  }

  // error in generate issue
  if ((verifyStatus.error as any)?.key === 107 && (verifyStatus.error as any)?.detail) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderIssuePromissoryFailed
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
          message={(verifyStatus.error as any)?.detail}
        />
      </PayGetOrderLayout>
    );
  }

  if (uploadSignatureState.error || verifyStatus.error) {
    return (
      <PayGetOrderLayout showHeaders={false} currentStepError>
        <PayGetOrderUploadSignatureUnAvailable />
      </PayGetOrderLayout>
    );
  }
  return (
    <>
      <PayGetOrderLayout showHeaders>
        <Flex vertical className="tw:h-full tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" justify="space-between">
          {currentStep === Steps.IMAGE_SIGNATURE ? (
            <PayGetOrderUploadSignatureDraw
              // 50kb
              maxSize={50 * 1024}
              onFinish={handleFinishSignature}
            />
          ) : null}
          {currentStep === Steps.INTRO ? (
            <GetOrderUploadVideoAndSignaturePageIntro
              onFinish={() => {
                setCurrentStep(Steps.START);
              }}
              onError={handleCameraErrors}
            />
          ) : null}
          {currentStep === Steps.START ? (
            <GetOrderUploadVideoAndSignaturePageStart
              notification={notification}
              onFinish={(file, sentence) => {
                if (imageFile) {
                  uploadSignature({ signatureImage: imageFile, signatureVideo: file, videoSentence: sentence });
                }
              }}
              loading={uploadSignatureState.loading || verifyStatus.loading}
            />
          ) : null}
          {currentStep === Steps.ACCESS_GUIDE ? <GetOrderUploadVideoAndSignaturePageAccessGuide /> : null}
        </Flex>
      </PayGetOrderLayout>
      {getOrderOpenInMobileModal ? (
        <ContinueWithMobileModal
          onClose={handleCloseGetOrderOpenInMobileModal}
          open={getOrderOpenInMobileModal}
          trackingCode={trackingCode}
        />
      ) : null}
      {getOrderOpenInSupportedDevicesModal ? (
        <ContinueWithSupportedDevices
          onClose={handleCloseGetOrderOpenInSupportedDevices}
          open={getOrderOpenInSupportedDevicesModal}
        />
      ) : null}
      {contextHolder}
    </>
  );
};

export default GetOrderUploadVideoAndSignaturePage;
